import React, { useState } from "react";
import { Container, Row, Table } from "reactstrap";
import { CenteredSpinner } from "../Common/CenteredSpinner";
import DriverDto from "../Common/dtos/DriverDto";
import KaAlert from "../Common/KaAlert";
import useDisplayMessage from "../Common/useDisplayMessage";
import FilterInput, { textSatisfiesFilter } from "../FilterInput";
import { useUser } from "../UserAccess/useUser";
import { CreateEditDriver } from "./CreateEditDriver";
import { modalMode } from "../Common/EntityModal";
import PermissionButton from "../UserAccess/PermissionButton";
import { Button } from "react-bootstrap";
import { HttpMethod } from "../Common/useFetch";
import useUserRequest, { UserRequestSpec } from "../Common/useUserRequest";
import Confirm from "../Common/Confirm";
import EditViewButton from "../UserAccess/EditViewButton";
import { usePermission } from "../UserAccess/usePermission";
import useGetDrivers from "../../requests/useGetDrivers";
import { SiteFilterPill, useSiteFilter } from "../UserAccess/SiteScope/SiteFilterPill";
import { SiteColumnCell, SiteColumnHeader } from "../Common/SiteColumn";
import { ActionsColumnHeader } from "../Common/ActionsColumn";

enum States {
  None,
  AddEditDriver,
}

type PopupState =
  {
    state: States.None;
    driver?: null;
  } |
  {
    state: States.AddEditDriver;
    driver: DriverDto | null;
  };

const removeRequestSpec = (businessId?: string, driverId?: string): UserRequestSpec => ({
  path: `/api/businesses/${businessId}/drivers/${driverId}`,
  method: HttpMethod.DELETE,
});


const alphabeticallyByName = (a: DriverDto, b: DriverDto) => a.name.localeCompare(b.name);

const DriverPage = () => {
  const user = useUser();
  const displayMessage = useDisplayMessage();
  const [filterText, setFilterText] = useState('');
  const [popupState, setPopupState] = useState<PopupState>({ state: States.None });
  const [driverToRemove, setDriverToRemove] = useState<DriverDto | undefined>(undefined);
  const canModify = usePermission('otherEntitiesModify');
  const siteFilter = useSiteFilter();

  const getDrivers = useGetDrivers();

  const containsFilterText = (driver: DriverDto) => {
    const satisfiesFilter = textSatisfiesFilter(filterText);
    return satisfiesFilter(driver.name) || satisfiesFilter(driver.number);
  };

  const filterTextChanged = (text: string) => setFilterText(text);

  const createClicked = () => {
    displayMessage.clear();
    setPopupState({ state: States.AddEditDriver, driver: null });
  }

  const editClicked = (driver: DriverDto) => {
    displayMessage.clear();
    setPopupState({ state: States.AddEditDriver, driver: driver });
  }

  const driverUpserted = (driverId: string, driverName: string) => {
    displayMessage.success(`Driver ${driverName} ${popupState.driver ? 'updated' : 'created'}`);
    getDrivers.query();
  }

  const editDismissed = () => setPopupState({ state: States.None });

  const removeSuccess = () => {
    displayMessage.success(`Driver ${driverToRemove?.name} removed`);
    return getDrivers.query();
  };

  const removeRequest = useUserRequest<void, void>(
    removeRequestSpec(user.selectedBusiness?.id, driverToRemove?.id), {
    onSuccess: removeSuccess,
    onError: (err) => displayMessage.fail(err.message),
    onComplete: () => setDriverToRemove(undefined),
  });

  const visibleDrivers = (getDrivers.data ?? [])
    .filter(containsFilterText)
    .filter(driver => siteFilter.matches(driver.sites))
    .sort(alphabeticallyByName);

  const loadingRows = getDrivers.isLoading || removeRequest.isLoading;

  if (getDrivers.isError) return <h3>{getDrivers.error?.message}</h3>

  return <Container>
    {popupState.state === States.AddEditDriver && <CreateEditDriver
      initialDriver={popupState.driver}
      onUpsert={driverUpserted}
      mode={modalMode(canModify, !popupState.driver)}
      onDismiss={editDismissed} />}
    <Confirm
      visible={!!driverToRemove}
      title='Remove driver'
      body={`Are you sure that you want to remove driver ${driverToRemove?.name}?`}
      isLoading={removeRequest.isLoading}
      onDismiss={() => setDriverToRemove(undefined)}
      onConfirm={() => removeRequest.request()} />
    <Row className='justify-content-between mb-2'>
      <h2 className='col-auto ka-blue'>Drivers</h2>
      <PermissionButton permission="otherEntitiesModify" className='col-auto btn-ghost-primary' onClick={createClicked}>Create Driver</PermissionButton>
    </Row>
    <KaAlert displayMessage={displayMessage.message} onClose={displayMessage.clear} />
    <form>
      <div className='row gx-2 gx-md-3 mb-3'>
        <div className='col-md-4 mb-2 mb-md-0'>
          <FilterInput placeholder='Search by name or number' onFilterTextChange={filterTextChanged} value={filterText} />
        </div>
        <div className='col-auto d-flex align-items-center'>
          <SiteFilterPill filter={siteFilter} />
        </div>
      </div>
    </form>
    <Table bordered className="preserve-whitespace" style={{tableLayout: "fixed"}}>
      <thead>
        <tr>
          <th>Name</th>
          <th className="col-2">Number</th>
          <SiteColumnHeader />
          <ActionsColumnHeader />
        </tr>
      </thead>
      <tbody>
        {!loadingRows && visibleDrivers.map(driver =>
          <tr key={driver.id}>
            <td>{driver.name}</td>
            <td>{driver.number}</td>
            <SiteColumnCell sites={driver.sites} />
            <td align="right" className="text-nowrap">
              <div className='dropdown'>
                <Button id='actionsMenuButton'
                  className='dropdown-toggle btn-ghost-secondary'
                  variant='link'
                  data-bs-toggle='dropdown'
                  aria-expanded='false'><strong>Actions</strong></Button>
                <div className='dropdown-menu' aria-labelledby='actionsMenuButton'>
                  <EditViewButton
                    canModify={canModify}
                    className='dropdown-item btn-ghost-secondary'
                    onClick={() => editClicked(driver)} />
                  <PermissionButton permission="otherEntitiesModify"
                    className='dropdown-item btn-ghost-secondary'
                    onClick={() => setDriverToRemove(driver)}>Remove</PermissionButton>
                </div>
              </div>
            </td>
          </tr>)}
        {(loadingRows || visibleDrivers.length === 0) &&
          <tr>
            <td colSpan={4} className='text-center text-muted py-3'>
              {loadingRows
                ? <CenteredSpinner />
                : siteFilter.emptyMessage('No drivers to show')}
            </td>
          </tr>}
      </tbody>
    </Table>
  </Container>
};

export { DriverPage };
